import {
  CustomerServiceStatusResponse,
  CustomerServiceStatus,
  CustomerServiceCreateRequest,
  CustomerServiceUpdateRequest,
  CustomerServiceApiError,
  ServiceCatalogResponse,
  ServiceCatalogQuery,
  ServiceCatalogItem,
  ServiceCatalogCreateRequest,
  ServiceCatalogUpdateRequest,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    public error: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function getCustomerServices(
  dataverseCustomerId: string,
  scope: 'ticket' | 'all' | 'activeOnly' | 'inactiveOnly' = 'ticket',
  signal?: AbortSignal
): Promise<CustomerServiceStatusResponse | null> {
  const endpoint = `${API_BASE_URL}/customers/${dataverseCustomerId}/services?scope=${scope}`;

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal,
    });

    if (response.status === 204) {
      return null;
    }

    if (!response.ok) {
      const errorData: CustomerServiceApiError = await response.json().catch(() => ({
        error: 'UnknownError',
        message: `Failed to fetch customer services: ${response.status}`,
        statusCode: response.status,
      }));
      throw new ApiError(response.status, errorData.error, errorData.message);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    if (error instanceof ApiError) {
      throw error;
    }
    console.error('Error fetching customer services:', error);
    return null;
  }
}

export async function createCustomerService(
  dataverseCustomerId: string,
  payload: CustomerServiceCreateRequest,
  signal?: AbortSignal
): Promise<CustomerServiceStatus> {
  const endpoint = `${API_BASE_URL}/customers/${dataverseCustomerId}/services`;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData: CustomerServiceApiError = await response.json().catch(() => ({
      error: 'UnknownError',
      message: `Failed to create customer service: ${response.status}`,
      statusCode: response.status,
    }));
    // sm4rt-api error bodies carry the human-readable text in `error` and no
    // `message` — without the fallback the modal showed a blank error (and a
    // Gorelo 409 "client already exists" would vanish).
    throw new ApiError(
      response.status,
      errorData.error,
      errorData.message || errorData.error || `Failed to create customer service: ${response.status}`
    );
  }

  const data = await response.json();
  return data.item;
}

export async function updateCustomerService(
  dataverseCustomerId: string,
  customerServiceId: string,
  payload: CustomerServiceUpdateRequest,
  signal?: AbortSignal
): Promise<CustomerServiceStatus> {
  const endpoint = `${API_BASE_URL}/customers/${dataverseCustomerId}/services/${customerServiceId}`;

  const response = await fetch(endpoint, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData: CustomerServiceApiError = await response.json().catch(() => ({
      error: 'UnknownError',
      message: `Failed to update customer service: ${response.status}`,
      statusCode: response.status,
    }));
    throw new ApiError(response.status, errorData.error, errorData.message);
  }

  const data = await response.json();
  return data.item;
}

export async function createServiceCatalogItem(
  payload: ServiceCatalogCreateRequest,
  signal?: AbortSignal
): Promise<ServiceCatalogItem> {
  const response = await fetch(`${API_BASE_URL}/services`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData: CustomerServiceApiError = await response.json().catch(() => ({
      error: 'UnknownError',
      message: `Failed to create service: ${response.status}`,
      statusCode: response.status,
    }));
    throw new ApiError(response.status, errorData.error, errorData.message);
  }

  const data = await response.json();
  return data.item;
}

export async function updateServiceCatalogItem(
  serviceId: string,
  payload: ServiceCatalogUpdateRequest,
  signal?: AbortSignal
): Promise<ServiceCatalogItem> {
  const response = await fetch(`${API_BASE_URL}/services/${serviceId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData: CustomerServiceApiError = await response.json().catch(() => ({
      error: 'UnknownError',
      message: `Failed to update service: ${response.status}`,
      statusCode: response.status,
    }));
    throw new ApiError(response.status, errorData.error, errorData.message);
  }

  const data = await response.json();
  return data.item;
}

export async function getServiceCatalog(
  query?: ServiceCatalogQuery,
  signal?: AbortSignal
): Promise<ServiceCatalogResponse> {
  const params = new URLSearchParams();

  if (query?.q) {
    params.append('q', query.q);
  }

  if (query?.activeOnly !== undefined) {
    params.append('activeOnly', query.activeOnly.toString());
  }

  const endpoint = `${API_BASE_URL}/services${params.toString() ? `?${params.toString()}` : ''}`;

  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    signal,
  });

  if (response.status === 204) {
    return { items: [] };
  }

  if (!response.ok) {
    const errorData: CustomerServiceApiError = await response.json().catch(() => ({
      error: 'UnknownError',
      message: `Failed to fetch service catalog: ${response.status}`,
      statusCode: response.status,
    }));
    throw new ApiError(response.status, errorData.error, errorData.message);
  }

  try {
    return await response.json();
  } catch (error) {
    console.warn('Service catalog returned no JSON body; treating as empty catalog.');
    return { items: [] };
  }
}
